import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { XMarkIcon, ClipboardDocumentIcon, CheckIcon } from "@heroicons/react/24/outline";

const OnboardingWizardModal = ({
  isOpen,
  onClose,
  onComplete,
  onNavigateToUsersTab,
  onPostJob,
  userId,
  companyName,
  initialStep = 0,
}) => {
  const [step, setStep] = useState(initialStep);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep(initialStep);
      setLinkCopied(false);
    }
  }, [isOpen, initialStep]);

  if (!isOpen) return null;

  const applicationLink = `${window.location.origin}/register?ref=${userId}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(applicationLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  const steps = [
    {
      id: "step-heading-0",
      heading: "Post a Job with Superstar Targets",
      content: (
        <div>
          <p className="text-sm text-lightgray mb-3">
            Superstar Targets are the most important part of your job post. They define who your ideal
            candidate looks like — the system matches every applicant against your targets' profiles.
          </p>
          <p className="text-sm text-lightgray mb-3">
            When you create your job, add at least one target — someone who already excels in the role.
            Better targets mean better matches.
          </p>
          <Link
            to="/how-to-pick-great-targets"
            className="text-lightblue underline hover:text-orange text-sm"
          >
            Learn how to pick great targets
          </Link>
        </div>
      ),
      ctaLabel: "Post Your First Job",
    },
    {
      id: "step-heading-1",
      heading: "Share Your Application Link",
      content: (
        <div>
          <p className="text-sm text-lightgray mb-3">
            Candidates who sign up through your link get auto-matched to your jobs. Share it in
            three places:
          </p>
          <ol className="list-decimal list-inside text-sm text-lightgray mb-3 space-y-1">
            <li>Your job posts and company website</li>
            <li>LinkedIn and social media</li>
            <li>Your email replies to applicants — so every applicant gets assessed</li>
          </ol>
          <p className="text-xs text-lightgray mb-3 italic">
            All candidates already in CareerMission are automatically matched to your jobs. This
            link adds more.
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={applicationLink}
              className="flex-1 p-2 rounded bg-deepblue text-lightgray border border-lightblue text-sm"
            />
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-1 bg-deepblue border border-lightblue text-lightgray py-2 px-3 rounded hover:bg-bluegray text-sm"
              aria-label="Copy your link"
            >
              <ClipboardDocumentIcon className="w-4 h-4" />
              Copy
            </button>
          </div>
          <div aria-live="polite" className="text-sm text-green-400 mt-1 h-5">
            {linkCopied ? "Link copied!" : ""}
          </div>
        </div>
      ),
      ctaLabel: "Next: Manage Your Team",
    },
    {
      id: "step-heading-2",
      heading: "Build Your Hiring Team",
      content: (
        <div>
          <p className="text-sm text-lightgray mb-3">
            Invite hiring managers and recruiters to your company. They can review candidates and
            manage job posts. You control who has access.
          </p>
          <button
            onClick={onNavigateToUsersTab}
            className="text-lightblue underline hover:text-orange cursor-pointer text-sm bg-transparent border-0 p-0"
          >
            Go to Manage Users
          </button>
        </div>
      ),
      ctaLabel: "Finish Setup",
    },
  ];

  const currentStep = steps[step];
  const isLastStep = step === steps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else if (step === 0 && onPostJob) {
      onPostJob();
    } else {
      setStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    setStep((s) => s - 1);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={currentStep.id}
        className="bg-bluegray p-6 rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto relative"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-lightgray hover:text-white"
          aria-label="Close"
          title="Close setup wizard"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-3 mb-6">
          {steps.map((s, idx) => (
            <div key={idx} className="flex items-center">
              <div
                className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                  idx < step
                    ? "bg-darkblue text-white"
                    : idx === step
                    ? "border-2 border-orange text-white"
                    : "border border-lightblue text-lightgray"
                }`}
                aria-current={idx === step ? "step" : undefined}
              >
                {idx < step ? <CheckIcon className="w-4 h-4" /> : idx + 1}
              </div>
              {idx < steps.length - 1 && (
                <div className="w-8 h-px bg-lightblue mx-1" />
              )}
            </div>
          ))}
        </div>

        {/* Step heading */}
        <h2
          id={currentStep.id}
          className="text-orange text-xl font-bold mb-4"
        >
          {currentStep.heading}
        </h2>

        {/* Step content */}
        <div className="mb-6">{currentStep.content}</div>

        {/* Navigation buttons */}
        <div className="flex justify-between items-center">
          <div>
            {step > 0 && (
              <button
                onClick={handleBack}
                className="bg-deepblue text-lightgray border border-lightblue py-2 px-4 rounded hover:bg-bluegray"
              >
                Back
              </button>
            )}
          </div>
          <button
            onClick={handleNext}
            className="bg-orange hover:bg-deeporange text-white font-bold py-2 px-4 rounded"
          >
            {currentStep.ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizardModal;
